/**
 * Every team's live scanner activity, for the admin live dashboard.
 *
 * The admin counterpart to the electron client's `useTrlTeamActivity`, which
 * watches one team. The server already emits `TRL_TEAM_ACTIVITY` to admins as well
 * as to the team it concerns (`emitToTeamAndAdmins`), and the request handler
 * honours an admin-supplied `teamId` — admin-web simply never subscribed.
 *
 * Same two resilience rules as the player-side hook, for the same reasons:
 *
 * - each push is a COMPLETE team snapshot, so state is replaced wholesale and
 *   there is no delta merging to get wrong;
 * - every transient entry carries an `expiresAt`, honoured locally, so a scan
 *   decays to idle on the dashboard even if the server's follow-up push is missed.
 *
 * Requests are STAGGERED across teams rather than fired in one tick: inbound
 * socket events are rate-limited, and a twelve-team game mounting the dashboard
 * would otherwise burst twelve requests simultaneously and have the tail dropped.
 * After the initial fill the push channel carries every change, so nothing here
 * polls.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSocketContext } from '@swtorbit/shared-react-hooks';
import {
  SocketEvent,
  type TrlPlayerActivity,
  type TrlTeamActivityBroadcast,
  type TrlTeamActivityRequest,
  type TrlTeamActivityResponse,
} from '@swtorbit/shared-socket-events';

export interface TrlAdminActivityConfig {
  enabled: boolean;
  roomCode: string | null;
  /** Team ids to watch. Usually every team in the game. */
  teamIds: string[];
}

/** Live activity by player id, across all teams. Absent players are idle. */
export type TrlAdminActivityMap = Record<string, TrlPlayerActivity>;

/** Gap between per-team requests on mount, to stay clear of the inbound rate limit. */
const REQUEST_STAGGER_MS = 120;

const EMPTY: TrlAdminActivityMap = {};

export function useTrlAdminActivity(config: TrlAdminActivityConfig): TrlAdminActivityMap {
  const { enabled, roomCode, teamIds } = config;
  const { socket } = useSocketContext();
  const [activity, setActivity] = useState<TrlAdminActivityMap>(EMPTY);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearTimers = useCallback(() => {
    for (const timer of timersRef.current) clearTimeout(timer);
    timersRef.current = [];
  }, []);

  // Join the ids so the effect re-runs when the roster actually changes, not on
  // every render that rebuilds the array.
  const teamKey = teamIds.join(',');

  useEffect(() => {
    clearTimers();
    if (!socket || !enabled || !roomCode || teamIds.length === 0) {
      setActivity(EMPTY);
      return;
    }

    /**
     * Merge one team's snapshot into the cross-team map.
     *
     * Unlike the single-team hook this MERGES rather than replaces, because each
     * push covers one team and replacing would blank every other team's members.
     * Within a team it is still a wholesale replace, so a member who has gone idle
     * is correctly dropped back rather than left lit.
     */
    const apply = (payload: TrlTeamActivityBroadcast) => {
      if (payload.roomCode !== roomCode) return;

      // The server's clock may differ from ours; anchor every expiry to the
      // snapshot's `emittedAt` so a skewed local clock can't expire entries early.
      const skewMs = Date.now() - payload.emittedAt;

      setActivity((prev) => {
        const next = { ...prev };
        for (const member of payload.members) next[member.playerId] = member.activity;
        return next;
      });

      for (const member of payload.members) {
        if (member.expiresAt == null) continue;
        const delayMs = member.expiresAt + skewMs - Date.now();
        // Self-healing: decay locally even if the server's idle push never lands.
        const timer = setTimeout(() => {
          setActivity((prev) =>
            prev[member.playerId] === undefined || prev[member.playerId] === 'idle'
              ? prev
              : { ...prev, [member.playerId]: 'idle' }
          );
        }, Math.max(0, delayMs));
        timersRef.current.push(timer);
      }
    };

    const requestTeam = (teamId: string) => {
      const payload: TrlTeamActivityRequest = { roomCode, teamId };
      socket.emit(SocketEvent.TRL_REQUEST_TEAM_ACTIVITY, payload, (res: TrlTeamActivityResponse) => {
        if (res?.success && res.state) apply(res.state);
      });
    };

    const requestAll = () => {
      teamIds.forEach((teamId, i) => {
        const timer = setTimeout(() => requestTeam(teamId), i * REQUEST_STAGGER_MS);
        timersRef.current.push(timer);
      });
    };

    socket.on(SocketEvent.TRL_TEAM_ACTIVITY, apply);
    // One pass on mount, one per reconnect — never polled.
    socket.on('connect', requestAll);
    requestAll();

    return () => {
      socket.off(SocketEvent.TRL_TEAM_ACTIVITY, apply);
      socket.off('connect', requestAll);
      clearTimers();
    };
    // `teamKey` stands in for `teamIds`; see its definition above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, enabled, roomCode, teamKey, clearTimers]);

  return activity;
}
